import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Button } from "@mui/material";
import { PositionsDataContext } from "../context/PositionsDataContext";
import { useNav } from "../context/NavContext";
import { DEFAULT_NAV_NUMBER } from "../config/config";
import {
  OPS_TYPE_IDS,
  PORTFOLIO_ALERT_TYPE,
  buildMessage,
  collectAlerts,
  findNewAlerts,
  loadSeenKeys,
  nextSeenKeys,
  saveSeenKeys,
} from "../utils/opsAlertNotifications";
import { findThresholdCrossings } from "../utils/portfolioThresholds";

// Ops alerts and Portfolio alerts are shown in two different places, so each gets
// its own notification that opens its own place.
const GROUPS = {
  ops: {
    noun: "Ops",
    tag: "ops-alerts",
    path: "/OpsPage",
    tabKey: "opsReportsPageActiveTab",
    tab: "tab6",
  },
  portfolio: {
    noun: "Portfolio",
    tag: "portfolio-alerts",
    path: "/",
    tabKey: "homeActiveTab",
    tab: "tab4",
  },
};

const canUseDesktopNotifications = () => typeof Notification !== "undefined";

// Mounted once for the whole app: announces new alerts on any page, as in-app
// toasts and, if the user allowed it, desktop notifications. It only announces;
// the alerts stay where they are (the Ops Alerts tab until the scheduled update
// drops them, the Portfolio Alerts tab while the threshold is crossed) whether
// or not anyone opens or closes a notification.
const OpsAlertNotifier = () => {
  const { datasets } = useContext(PositionsDataContext);
  const nav = useNav();
  const navigate = useNavigate();
  const [messages, setMessages] = useState({ ops: null, portfolio: null });
  const [permission, setPermission] = useState(
    canUseDesktopNotifications() ? Notification.permission : "denied"
  );

  const opsData = datasets?.opsData;
  const positions = datasets?.positions;
  const previousDay = datasets?.previousDay;

  const dismiss = (group) =>
    setMessages((prev) => ({ ...prev, [group]: null }));

  const openGroup = (group) => {
    const { path, tabKey, tab } = GROUPS[group];
    try {
      localStorage.setItem(tabKey, tab);
    } catch {
      // Falls back to whichever tab the page opens on.
    }
    dismiss(group);
    navigate(path);
  };

  useEffect(() => {
    // Each source is only evaluated once its data has arrived. The context starts
    // with empty lists, and the fund value starts at a default until it is fetched.
    const opsReady =
      Boolean(opsData) && typeof opsData === "object" && !Array.isArray(opsData);
    const portfolioReady =
      Array.isArray(positions) &&
      positions.length > 0 &&
      Array.isArray(previousDay) &&
      previousDay.length > 0 &&
      nav > 0 &&
      nav !== DEFAULT_NAV_NUMBER;

    const readyTypes = [
      ...(opsReady ? OPS_TYPE_IDS : []),
      ...(portfolioReady ? [PORTFOLIO_ALERT_TYPE] : []),
    ];
    if (readyTypes.length === 0) return;

    const current = [
      ...(opsReady ? collectAlerts(opsData) : []),
      ...(portfolioReady ? findThresholdCrossings(positions, previousDay, nav) : []),
    ];
    const seen = loadSeenKeys();
    const newAlerts = findNewAlerts(current, seen);

    // Keep only alerts still present, so one that clears and returns notifies again.
    saveSeenKeys(nextSeenKeys(seen, current, readyTypes));

    if (newAlerts.length === 0) return;

    const byGroup = {
      ops: newAlerts.filter((alert) => alert.type !== PORTFOLIO_ALERT_TYPE),
      portfolio: newAlerts.filter((alert) => alert.type === PORTFOLIO_ALERT_TYPE),
    };
    const updates = {};
    Object.entries(byGroup).forEach(([group, alerts]) => {
      if (alerts.length === 0) return;
      const { noun, tag } = GROUPS[group];
      const message = buildMessage(alerts, noun);
      updates[group] = message;

      if (canUseDesktopNotifications() && Notification.permission === "granted") {
        const notification = new Notification(message.title, {
          body: message.body,
          tag,
        });
        notification.onclick = () => {
          window.focus();
          openGroup(group);
          notification.close();
        };
      }
    });
    setMessages((prev) => ({ ...prev, ...updates }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opsData, positions, previousDay, nav]);

  const enableDesktopAlerts = async () => {
    setPermission(await Notification.requestPermission());
  };

  const visible = Object.keys(GROUPS).filter((group) => messages[group]);
  if (visible.length === 0) return null;

  // No auto-hide and no click-away close: each toast stays until it is opened or closed.
  return (
    <Box
      sx={{
        position: "fixed",
        top: 24,
        right: 24,
        zIndex: (theme) => theme.zIndex.snackbar,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        maxWidth: "calc(100vw - 48px)",
      }}
    >
      {visible.map((group, index) => (
        <Alert
          key={group}
          severity="warning"
          variant="filled"
          action={
            <>
              {index === 0 && permission === "default" && (
                <Button color="inherit" size="small" onClick={enableDesktopAlerts}>
                  Enable desktop alerts
                </Button>
              )}
              <Button color="inherit" size="small" onClick={() => openGroup(group)}>
                Open
              </Button>
              <Button color="inherit" size="small" onClick={() => dismiss(group)}>
                Close
              </Button>
            </>
          }
          sx={{ whiteSpace: "pre-line", alignItems: "center" }}
        >
          <strong>{messages[group].title}</strong>
          {"\n"}
          {messages[group].body}
        </Alert>
      ))}
    </Box>
  );
};

export default OpsAlertNotifier;
